import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/query-keys';
import { apiFetch } from '@/lib/api/client';

export interface User {
  id: string;
  email: string;
  name: string;
  roles?: string[];
  avatarUrl?: string;
}

function mapUserResponse(payload: unknown): User {
  if (!payload || typeof payload !== 'object') {
    throw new Error('Invalid user payload received from API.');
  }

  const record = payload as Record<string, unknown>;
  const idValue = record.id;
  const email = typeof record.email === 'string' ? record.email : '';
  const fullName = typeof record.name === 'string' ? record.name : undefined;
  const firstName = typeof record.first_name === 'string' ? record.first_name : undefined;
  const lastName = typeof record.last_name === 'string' ? record.last_name : undefined;
  const normalizedRoles = Array.isArray(record.roles)
    ? record.roles
        .map((role) => {
          if (typeof role === 'string') {
            return role;
          }

          if (role && typeof role === 'object') {
            const roleRecord = role as Record<string, unknown>;
            const possibleKeys = ['role', 'name', 'key', 'value'];
            for (const key of possibleKeys) {
              const value = roleRecord[key];
              if (typeof value === 'string') {
                return value;
              }
            }
          }

          return undefined;
        })
        .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
    : undefined;

  const singleRole =
    typeof record.user_role === 'string' && record.user_role.trim().length > 0
      ? [record.user_role]
      : undefined;

  const roles = normalizedRoles && normalizedRoles.length > 0 ? normalizedRoles : singleRole;
  const avatarUrl =
    typeof record.profile_picture_url === 'string' && record.profile_picture_url.length > 0
      ? record.profile_picture_url
      : typeof record.profile_photo_url === 'string' && record.profile_photo_url.length > 0
        ? record.profile_photo_url
        : typeof record.avatar_url === 'string' && record.avatar_url.length > 0
          ? record.avatar_url
          : typeof record.avatar === 'string' && record.avatar.length > 0
            ? record.avatar
            : undefined;

  const computedName =
    fullName ||
    [firstName, lastName].filter((part): part is string => Boolean(part)).join(' ') ||
    email ||
    'User';

  const id = idValue != null ? String(idValue) : 'unknown';

  return {
    id,
    email,
    name: computedName,
    roles,
    avatarUrl,
  };
}

async function fetchCurrentUser(): Promise<User> {
  const response = await apiFetch('/api/v1/users/me');

  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      throw new Error('Not authenticated');
    }

    throw new Error(`Failed to fetch user: ${response.statusText}`);
  }

  const data = await response.json().catch(() => null);
  const payload = Array.isArray(data) ? data[0] : data;

  if (!payload) {
    throw new Error('User payload is empty.');
  }

  return mapUserResponse(payload);
}

export function useUser() {
  return useQuery({
    queryKey: queryKeys.user,
    queryFn: fetchCurrentUser,
    retry: false,
  });
}
